import { apiRequest } from '../../../shared/services/apiClient';

export type CallSessionStatus =
  | 'initiating'
  | 'ringing'
  | 'inProgress'
  | 'onHold'
  | 'ended'
  | 'failed'
  | 'cancelled';

export interface CallingCapabilities {
  connectorConfigured: boolean;
  outboundEnabled: boolean;
  inboundEnabled: boolean;
  callControlEnabled: boolean;
  hasEmployeeExtension: boolean;
  employeeExtension?: string | null;
  channelAccountId?: string | null;
}

export interface CallSession {
  id: string;
  channelAccountId: string;
  conversationId?: string | null;
  contactId?: string | null;
  employeeId: string;
  provider: string;
  direction: string;
  status: CallSessionStatus;
  externalCallId?: string | null;
  fromNumber: string;
  toNumber: string;
  employeeExtension: string;
  startedAtUtc: string;
  answeredAtUtc?: string | null;
  endedAtUtc?: string | null;
  durationSeconds?: number | null;
  failureReason?: string | null;
  contactName?: string | null;
  contactDisplayNumber?: string | null;
  employeeName?: string | null;
  /** Set instead of the contact fields when the call was placed to a colleague's extension. */
  peerEmployeeId?: string | null;
  peerEmployeeName?: string | null;
  answeredByAi?: boolean;
}

export interface CallHistoryQuery {
  q?: string;
  direction?: 'Inbound' | 'Outbound';
  status?: string;
  contactId?: string;
  employeeId?: string;
  dateFrom?: string;
  dateTo?: string;
  includeTeam?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CallHistoryPage {
  items: CallSession[];
  page: number;
  pageSize: number;
  total: number;
  hasNext: boolean;
}

/** Dial either a contact or a colleague's extension, never both. */
export interface StartOutboundCallRequest {
  contactId?: string;
  contactChannelId?: string;
  phoneNumber?: string;
  targetEmployeeId?: string;
  channelAccountId?: string;
}

export interface RegisterInboundCallRequest {
  phoneNumber: string;
  callerName?: string;
  externalCallId?: string;
  channelAccountId?: string;
}

export interface InboundPopSettings {
  channelAccountId: string;
  crmLookupSecret: string;
  popUrlTemplate: string;
  lookupUrlTemplate: string;
}

const pickSingle = (payload: unknown): unknown => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const envelope = payload as Record<string, unknown>;
  if (envelope.data && typeof envelope.data === 'object') {
    return envelope.data;
  }

  return payload;
};

const asRecord = (payload: unknown): Record<string, unknown> =>
  payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};

const optionalString = (value: unknown): string | null => (value ? String(value) : null);

export const normalizeCallSession = (payload: unknown): CallSession => {
  const value = asRecord(payload);
  return {
    id: String(value.id ?? ''),
    channelAccountId: String(value.channelAccountId ?? ''),
    conversationId: optionalString(value.conversationId),
    contactId: optionalString(value.contactId),
    employeeId: String(value.employeeId ?? ''),
    provider: String(value.provider ?? ''),
    direction: String(value.direction ?? ''),
    status: String(value.status ?? 'initiating') as CallSessionStatus,
    externalCallId: optionalString(value.externalCallId),
    fromNumber: String(value.fromNumber ?? ''),
    toNumber: String(value.toNumber ?? ''),
    employeeExtension: String(value.employeeExtension ?? ''),
    startedAtUtc: String(value.startedAtUtc ?? ''),
    answeredAtUtc: optionalString(value.answeredAtUtc),
    endedAtUtc: optionalString(value.endedAtUtc),
    durationSeconds: typeof value.durationSeconds === 'number' ? value.durationSeconds : null,
    failureReason: optionalString(value.failureReason),
    contactName: optionalString(value.contactName),
    contactDisplayNumber: optionalString(value.contactDisplayNumber),
    employeeName: optionalString(value.employeeName),
    peerEmployeeId: optionalString(value.peerEmployeeId),
    peerEmployeeName: optionalString(value.peerEmployeeName),
    answeredByAi: Boolean(value.answeredByAi),
  };
};

/** The other party on a call, whichever kind of call it is. */
export const getCallCounterpartName = (session: CallSession): string | null =>
  session.peerEmployeeName ?? session.contactName ?? null;

export const isActiveCallStatus = (status: CallSessionStatus): boolean =>
  status === 'initiating' || status === 'ringing' || status === 'inProgress' || status === 'onHold';

export const getCallingCapabilities = async (): Promise<CallingCapabilities> => {
  const value = asRecord(pickSingle(await apiRequest<unknown>('/api/v1/calls/capabilities')));
  return {
    connectorConfigured: Boolean(value.connectorConfigured),
    outboundEnabled: Boolean(value.outboundEnabled),
    inboundEnabled: Boolean(value.inboundEnabled),
    callControlEnabled: Boolean(value.callControlEnabled),
    hasEmployeeExtension: Boolean(value.hasEmployeeExtension),
    employeeExtension: optionalString(value.employeeExtension),
    channelAccountId: optionalString(value.channelAccountId),
  };
};

export const startOutboundCall = async (request: StartOutboundCallRequest): Promise<CallSession> =>
  normalizeCallSession(pickSingle(await apiRequest<unknown>('/api/v1/calls', {
    method: 'POST',
    body: JSON.stringify(request),
  })));

export const getActiveCallSessions = async (): Promise<CallSession[]> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/calls/active'));
  return Array.isArray(payload) ? payload.map(normalizeCallSession) : [];
};

export const getCallHistory = async (query: CallHistoryQuery = {}): Promise<CallHistoryPage> => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  const search = params.toString();
  const value = asRecord(pickSingle(await apiRequest<unknown>(`/api/v1/calls${search ? `?${search}` : ''}`)));
  return {
    items: Array.isArray(value.items) ? value.items.map(normalizeCallSession) : [],
    page: typeof value.page === 'number' ? value.page : 1,
    pageSize: typeof value.pageSize === 'number' ? value.pageSize : 25,
    total: typeof value.total === 'number' ? value.total : 0,
    hasNext: Boolean(value.hasNext),
  };
};

export const registerInboundCall = async (request: RegisterInboundCallRequest): Promise<CallSession> =>
  normalizeCallSession(pickSingle(await apiRequest<unknown>('/api/v1/calls/inbound', {
    method: 'POST',
    body: JSON.stringify(request),
  })));

const runCallCommand = async (
  callSessionId: string,
  command: string,
  body?: Record<string, unknown>,
): Promise<CallSession> =>
  normalizeCallSession(pickSingle(await apiRequest<unknown>(`/api/v1/calls/${callSessionId}/${command}`, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  })));

export const answerCall = (callSessionId: string) => runCallCommand(callSessionId, 'answer');
export const holdCall = (callSessionId: string) => runCallCommand(callSessionId, 'hold');
export const resumeCall = (callSessionId: string) => runCallCommand(callSessionId, 'resume');
export const hangupCall = (callSessionId: string) => runCallCommand(callSessionId, 'hangup');
export const transferCall = (callSessionId: string, target: string) =>
  runCallCommand(callSessionId, 'transfer', { target });

export const getInboundPopSettings = async (channelAccountId?: string): Promise<InboundPopSettings> => {
  const query = channelAccountId ? `?channelAccountId=${encodeURIComponent(channelAccountId)}` : '';
  const value = asRecord(pickSingle(await apiRequest<unknown>(`/api/v1/calls/inbound-pop/settings${query}`)));
  return {
    channelAccountId: String(value.channelAccountId ?? ''),
    crmLookupSecret: String(value.crmLookupSecret ?? ''),
    popUrlTemplate: String(value.popUrlTemplate ?? ''),
    lookupUrlTemplate: String(value.lookupUrlTemplate ?? ''),
  };
};
